import { toast } from 'sonner';
import { PenToolIcon as Tool } from 'lucide-react';
import GroupedMachineCard from '@/components/common/GroupedMachineCard';
import AddMachineModal from '@/redux/auth/AddMachine/AddMachineModal';
import { IUser } from '@/redux/auth/auth';
import { useDeleteMachineMutation } from '@/redux/auth/auth.api.slice';
import { groupMachinesByBrandAndModel } from '@/lib/utils';

const groupedMachinesOf = (user: IUser) =>
  groupMachinesByBrandAndModel(
    (user.ownedMachines || []).map((machine) => ({
      brand: machine.cncBrand.name,
      model: machine.cncModel.name,
      brandLink: machine.link,
      modelLink: machine.cncModel?.link,
      modelPicture: machine.cncModel?.picture,
      variant: {
        id: machine._id,
        name: machine.name,
        link: machine.link,
      },
    }))
  );

export default function MachinesSection({
  user,
  isOwnProfile,
}: {
  user: IUser;
  isOwnProfile: boolean;
}) {
  const [deleteMachine] = useDeleteMachineMutation();
  const machines = groupedMachinesOf(user);

  const handleDeleteMachine = async (machineId: string) => {
    try {
      await deleteMachine(machineId).unwrap();
      toast.success('Machine deleted successfully');
    } catch {
      toast.error('Failed to delete machine');
    }
  };

  return (
    <section className="flex flex-col gap-3" data-testid="profile-machines">
      {isOwnProfile && (
        <div className="flex justify-end">
          <AddMachineModal user={user} />
        </div>
      )}
      {machines.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Tool className="h-4 w-4 shrink-0" />
          {isOwnProfile
            ? 'You haven’t added any CNC machines yet. Click Add Machine to get started.'
            : 'No CNC machines added yet.'}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {machines.map((machine) => (
            <GroupedMachineCard
              key={`${machine.brand}-${machine.model}`}
              brand={machine.brand}
              model={machine.model}
              variants={machine.variants}
              brandLink={machine.brandLink}
              modelLink={machine.modelLink}
              modelPicture={machine.modelPicture}
              onDelete={isOwnProfile ? handleDeleteMachine : undefined}
            />
          ))}
        </div>
      )}
    </section>
  );
}
